import {
  MIN_PER_HOUR,
  SEC_PER_MIN,
  SUPPORTED_UNITS,
} from '@/helpers/unit-formatter/unit-formatter.constants';
import { UnitValue } from '@/helpers/unit-formatter/unit-formatter.types';
import {
  fpDivide,
  secondsToDuration,
  toNumber,
} from '@/helpers/unit-formatter/unit-formatter.utils';

/**
 * Transforms time values from seconds to other time units.
 * Supports conversion to minutes (min) and hours (hrs).
 * Uses floating-point safe arithmetic operations for accurate conversions.
 *
 * @param {UnitValue} unitValue - The time value in seconds
 * @param {any} unitValue.value - The numeric time value in seconds
 * @param {string} [unitValue.units] - The current units (should be 'sec' or similar)
 * @param {string} [newUnits] - Target time unit ('min', 'hrs')
 * @returns {UnitValue} Transformed time value in the target units
 *   - Returns original value if newUnits is not recognized
 *   - Returns original value if input value is null/undefined
 *
 * @example
 * transformSec({ value: 60, units: 'sec' }, 'min')
 * // Returns: { value: 1, units: 'min' }
 *
 * @example
 * transformSec({ value: 90, units: 'sec' }, 'min')
 * // Returns: { value: 1.5, units: 'min' }
 *
 * @example
 * transformSec({ value: 3600, units: 'sec' }, 'hrs')
 * // Returns: { value: 1, units: 'hrs' }
 */
export const transformSec = (unitValue: UnitValue, newUnits?: string): UnitValue => {
  const value = toNumber(unitValue.value);
  if (value == null || value == undefined) return unitValue;

  switch (newUnits?.toLowerCase()) {
    case SUPPORTED_UNITS.MIN:
      return {
        value: fpDivide(value, SEC_PER_MIN),
        units: newUnits,
      };
    case SUPPORTED_UNITS.HRS:
      return {
        value: fpDivide(value, SEC_PER_MIN * MIN_PER_HOUR),
        units: newUnits,
      };
    case SUPPORTED_UNITS.TIMER:
    case SUPPORTED_UNITS.DURATION:
      return {
        value: secondsToDuration(value),
        units: '',
      };
    default:
      return unitValue;
  }
};
